// Bolt-On Screening — shared catchment vocabulary + geo helpers.
//
// The panel answers "within reach of THIS point, which independent operators
// could be acquired to add density, and who is already rolling up here?"
// Catchment is HYBRID and honest per the handover:
//   - isochrone  — true drive-time, computed server-side via an external
//                  routing API (OpenRouteService); the strong mode.
//   - radius     — straight-line haversine fallback where no routable network
//                  / no API; the band is expressed as an EQUIVALENT km radius
//                  (band × a km-per-minute speed factor) and the boundary is
//                  rendered dashed + "approx (radius)" so it's never silently
//                  overstated across a river / city / estuary.
// Every catchment states its mode; a catchment never silently mixes modes.

export type CatchmentMode = 'isochrone' | 'radius';

/** Travel mode — driving (default, wider), walking (urban, tighter), or public
 *  transport (no routable isochrone profile → honest radius fallback). */
export type TravelMode = 'drive' | 'walk' | 'transit';
export const DEFAULT_TRAVEL_MODE: TravelMode = 'drive';

/** Travel-time bands (minutes) offered per mode — the picker's options. */
export const CATCHMENT_BANDS_BY_MODE: Record<TravelMode, readonly number[]> = {
  drive: [10, 20, 40],
  walk: [15, 30, 45],
  transit: [20, 40, 60],
};
/** Flat union of every valid band, for server-side validation. */
export const CATCHMENT_BANDS = [10, 15, 20, 30, 40, 45, 60] as const;
export type CatchmentBand = number;
/** Default band per mode (the mid option). */
export const DEFAULT_CATCHMENT_BAND_BY_MODE: Record<TravelMode, number> = {
  drive: 20,
  walk: 30,
  transit: 40,
};
export const DEFAULT_CATCHMENT_BAND: CatchmentBand =
  DEFAULT_CATCHMENT_BAND_BY_MODE[DEFAULT_TRAVEL_MODE];

/** OpenRouteService routing profile per travel mode (isochrone endpoint). Public
 *  transport has no ORS isochrone profile, so it is absent here — those
 *  catchments resolve via the labelled radius fallback. */
export const ORS_PROFILE: Partial<Record<TravelMode, string>> = {
  drive: 'driving-car',
  walk: 'foot-walking',
};

// Radius-fallback speed factor: km covered per minute of the band, by mode. A
// blended UK driving speed (~48 km/h), a walking pace (~5 km/h), and a
// door-to-door public-transport effective speed (~24 km/h, incl. access/wait/
// transfers). Deliberately approximate — the isochrone (per-profile) is the
// accurate path; radius is the labelled fallback. CONFIRM-AT-BUILD: refine per
// market / urbanity.
export const SPEED_KM_PER_MIN: Record<TravelMode, number> = {
  drive: 0.8,
  walk: 0.083,
  transit: 0.4,
};

/** Equivalent straight-line radius (km) for a travel-time band, by mode. */
export function bandToRadiusKm(band: number, travel: TravelMode = 'drive'): number {
  return band * SPEED_KM_PER_MIN[travel];
}

/** Lat/lng bounding box around a point for a given radius (km). Used as the
 *  cheap, indexed prefilter before exact haversine / point-in-polygon. */
export function bboxForRadiusKm(
  lat: number,
  lng: number,
  km: number,
): { latMin: number; latMax: number; lngMin: number; lngMax: number } {
  const dLat = km / 111.32; // 1° latitude ≈ 111.32 km
  const cos = Math.cos((lat * Math.PI) / 180);
  const dLng = km / (111.32 * Math.max(cos, 1e-6)); // guard near the poles
  return { latMin: lat - dLat, latMax: lat + dLat, lngMin: lng - dLng, lngMax: lng + dLng };
}

/** Great-circle distance (km) between two WGS84 points. */
export function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Ray-casting point-in-polygon. `ring` is [lng, lat] pairs (GeoJSON order),
 *  used to refine the bbox-prefiltered candidates against an isochrone polygon. */
export function pointInRing(
  lat: number,
  lng: number,
  ring: ReadonlyArray<[number, number]>,
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    const intersect = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// How each in-catchment site is classified for the map layers:
//   target     — actionable bolt-on: ≥€150k independent of the subsegment.
//   competitor — already in a resolved economic group (being rolled up).
//   neutral    — ownership unknown / unmatched; context only.
export type BoltOnClass = 'target' | 'competitor' | 'neutral';

// --- Subsegment- and urbanity-specific catchments ---------------------------
//
// A catchment is NOT one-size-fits-all: it depends on the service model and on
// how dense the surroundings are. A community pharmacy is a walk-in, ½-mile
// affair; a care home is chosen within family visiting distance; domiciliary
// care is a carer travel-radius. We express each as a travel MODE + TIME and
// let the isochrone self-scale the geographic footprint (a 15-min drive covers
// far less ground in a city than in the country). Urbanity then nudges mode +
// time: walk-in services switch to driving in rural areas, and drive catchments
// tighten in dense urban cores (more competition, shorter trips) and widen in
// sparse rural ones.
//
// Anchors (literature, not one canonical source): NHS pharmaceutical-needs
// access norms (pharmacy), GP practice boundaries, retail drive-time catchment
// analysis (dental/optical/vet), care-home visiting-distance, domiciliary
// carer-radius. Defaults are defensible v1 heuristics, refineable per market.

interface SubsegmentCatchment {
  mode: TravelMode;
  minutes: number;
  /** Walk-in, high-frequency local service (pharmacy, GP): drives in rural. */
  walkIn?: boolean;
}

/** Base catchment per CANONICAL subsegment. null = B2B distribution — no
 *  residential catchment is meaningful (suppress the catchment view). */
export const CATCHMENT_BASE: Readonly<Record<string, SubsegmentCatchment | null>> = {
  Pharmacy: { mode: 'walk', minutes: 10, walkIn: true },
  'Primary care (GP)': { mode: 'walk', minutes: 15, walkIn: true },
  Dental: { mode: 'drive', minutes: 15 },
  Optical: { mode: 'drive', minutes: 15 },
  'Physiotherapy & allied health': { mode: 'drive', minutes: 15 },
  Audiology: { mode: 'drive', minutes: 20 },
  'Diagnostics & labs': { mode: 'drive', minutes: 20 },
  Veterinary: { mode: 'drive', minutes: 15 },
  'Residential elderly care': { mode: 'drive', minutes: 30 },
  'Residential specialist care': { mode: 'drive', minutes: 40 },
  'Domiciliary / home care': { mode: 'drive', minutes: 30 },
  'Hospitals & clinics': { mode: 'drive', minutes: 40 },
  'Specialist medical': { mode: 'drive', minutes: 40 },
  'Ambulance / patient transport': { mode: 'drive', minutes: 45 },
  'Pharma wholesale': null,
  'Medical equipment & supplies': null,
  'Other healthcare': { mode: 'drive', minutes: 20 },
};

export type Urbanity = 'urban' | 'suburban' | 'rural';

/** Classify a site's surroundings from its local population density (people per
 *  km²). Thresholds align roughly with ONS urban/rural density bands; null →
 *  suburban (the neutral default). */
export function urbanityOf(densityPerKm2: number | null | undefined): Urbanity {
  if (densityPerKm2 == null) return 'suburban';
  if (densityPerKm2 >= 2500) return 'urban';
  if (densityPerKm2 < 400) return 'rural';
  return 'suburban';
}

/** Move a time value up/down the allowed-band ladder (so the result is always a
 *  valid isochrone band). */
function stepBand(minutes: number, by: number): number {
  const i = CATCHMENT_BANDS.indexOf(
    CATCHMENT_BANDS.reduce((a, b) => (Math.abs(b - minutes) < Math.abs(a - minutes) ? b : a)),
  );
  const j = Math.max(0, Math.min(CATCHMENT_BANDS.length - 1, i + by));
  return CATCHMENT_BANDS[j]!;
}

/** Resolve a site's catchment = base subsegment reach, adjusted for urbanity
 *  from the site's local density. null = no residential catchment (B2B). */
export function catchmentForSite(
  subsegment: string | null | undefined,
  densityPerKm2: number | null | undefined,
): { mode: 'walk' | 'drive'; minutes: number; urbanity: Urbanity } | null {
  const base = subsegment ? CATCHMENT_BASE[subsegment] : null;
  if (!base) return null;
  const urbanity = urbanityOf(densityPerKm2);
  if (base.walkIn) {
    // Walk in town; in the country there's no operator within walking distance,
    // so customers drive to the nearest one.
    if (urbanity === 'rural') return { mode: 'drive', minutes: 15, urbanity };
    if (urbanity === 'suburban')
      return { mode: 'walk', minutes: stepBand(base.minutes, 1), urbanity };
    return { mode: 'walk', minutes: base.minutes, urbanity };
  }
  // Drive catchments: tighter in the dense city, wider in the sparse country.
  const by = urbanity === 'urban' ? -1 : urbanity === 'rural' ? 1 : 0;
  return { mode: 'drive', minutes: stepBand(base.minutes, by), urbanity };
}
